import { Type } from "class-transformer";
import { CalendarEventStartRelativeTo } from "./CalendarEventStartRelativeTo";
import { CalendarEventTemplate } from "./CalendarEventTemplate";
import { GenericDataType } from "src/modules/enaio/netgen/GenericDataType";
import { CalendarEventType } from "./CalendarEventType";
import { CalendarOutlineEventRole } from "./CalendarOutlineEventRole";
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from "src/modules/utils/shared/utils";
import { CalendarEventReminderType } from "./CalendarEventReminderType";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { inject } from "@angular/core";

export class CalendarOutlineEvent {
    
    static attribs: { name: string; type: GenericDataType }[] = [
        { name: "title", type: GenericDataType.string },
        { name: "typeId", type: GenericDataType.int },
        { name: "isMilestone", type: GenericDataType.bool },
        { name: "onlyWeekdays", type: GenericDataType.bool }, 
        { name: "durationDays", type: GenericDataType.int },
        { name: "workingTime", type: GenericDataType.decimal },
        { name: "startRelativeTo", type: GenericDataType.string },
        { name: "startRelativeDays", type: GenericDataType.int },
        { name: "roles", type: GenericDataType.array },
        { name: "reminderType", type: GenericDataType.int },
        { name: "reminderDeltaMinutes", type: GenericDataType.int }
    ];

    //netgen start
    structurePath: string;
    sortIndex = 0;
    templateId: number = null;
    outlineTemplateId: number = null;
    title: string;
    description: string;
    typeId: number = null;
    @Type(() => CalendarEventType)
    type: CalendarEventType;
    isMilestone: boolean = null;
    onlyWeekdays: boolean = null;
    startRelativeTo: CalendarEventStartRelativeTo = null;
    startRelativeDays: number = null;
    startRelativeToEventId: number = null;
    keepStartRelativeTo = false;
    allDay: boolean = null;
    durationDays: number = null;
    workingTime: number = null;
    contactPersonRoleId: number = null;
    inheritRoles = true;
    @Type(() => CalendarOutlineEventRole)
    roles: CalendarOutlineEventRole[];
    reminderType: CalendarEventReminderType = CalendarEventReminderType.none;
    reminderDeltaMinutes: number = null;
    id = 0;
    //netgen end

    template: CalendarEventTemplate;
    fromOutlineTemplateId: number = null;

    setTemplate(template: CalendarEventTemplate): void {
         this.templateId = template.id;
         this.template = template;
         this.title = null;
         this.typeId = null;
         this.description = null;
         this.isMilestone = null;
         this.durationDays = null;
         this.onlyWeekdays = null;
         this.startRelativeTo = null;
         this.startRelativeDays = null;
         this.allDay = null;
         this.workingTime = null; 
         this.reminderType = null;
         this.reminderDeltaMinutes = null;
         this.inheritRoles = true;
    }

    copyFromTemplate(template: CalendarEventTemplate): void {
         this.template = Objects.cloneDeep(template);
         this.templateId = this.template.id;
         this.template.title = this.title ?? this.template.title;
         this.template.typeId = this.typeId ?? this.template.typeId;
         this.template.description = this.description ?? this.template.description;
         this.template.isMilestone = this.isMilestone ?? this.template.isMilestone;
         this.template.durationDays = this.durationDays ?? this.template.durationDays;
         this.template.onlyWeekdays = this.onlyWeekdays ?? this.template.onlyWeekdays;
         this.template.startRelativeTo = this.startRelativeTo ?? this.template.startRelativeTo;
         this.template.startRelativeDays = this.startRelativeDays ?? this.template.startRelativeDays;
         this.template.allDay = this.allDay ?? this.template.allDay;
         this.template.workingTime = this.workingTime ?? this.template.workingTime;   
         this.template.reminderType = this.reminderType ?? this.template.reminderType;
         this.template.reminderDeltaMinutes = this.reminderDeltaMinutes ?? this.template.reminderDeltaMinutes;
         this.title = null;
         this.description = null;
         this.isMilestone = null;
         this.durationDays = null;
         this.onlyWeekdays = null;
         this.startRelativeTo = null;
         this.startRelativeDays = null;
         this.allDay = null;
         this.workingTime = null;  
         this.reminderType = null;
         this.reminderDeltaMinutes = null;
    }

    moveTemplateDataToLocal(): this {
         this.title ??= this.template.title;
         this.typeId ??= this.template.typeId;
         this.description ??= this.template.description;
         this.isMilestone ??= this.template.isMilestone;
         this.durationDays ??= this.template.durationDays;
         this.onlyWeekdays ??= this.template.onlyWeekdays;
         this.startRelativeTo ??= this.template.startRelativeTo;
         this.startRelativeDays ??= this.template.startRelativeDays;
         this.allDay ??= this.template.allDay;
         this.workingTime ??= this.template.workingTime; 
         this.reminderType ??= this.template.reminderType;
         this.reminderDeltaMinutes ??= this.template.reminderDeltaMinutes;
         return this;
    }

    getTitle(): string {
        return this.getAttrib("title");
    }

    getStartRelativeTo(): CalendarEventStartRelativeTo {
        return this.getAttrib("startRelativeTo");
    }

    getStartRelativeDays(): number {
        return this.getAttrib("startRelativeDays");
    }

    getAttrib(attribName: string): any {
        let attrib = CalendarOutlineEvent.attribs.find(a => a.name == attribName);
        if (attrib == null) {
            throw new Error(inject(MainAppService).t("entity.CalendarOutlineEvent.other.errorAttribNotFound", { attribName }) ?? "Attribute '" + attribName + "' was not found");
        }
        return (this.isOverridden(attribName) ? this[attribName] : this.template != null ? this.template[attribName] : null) ?? GenericDataType.getDefaultValue(attrib.type);
    }

    isOverridden(attribName: string): boolean {
        let value = this[attribName];
        
        if (attribName == "roles") {
            return !this.inheritRoles || !Utils.isNoe(this.roles);
        }
        else {
            return value != null;
        }
    }

    getRelativeStartDate(events: CalendarOutlineEvent[]): { startRelativeTo: CalendarEventStartRelativeTo, days: number } {
        let processedIds = new Set<number>();
        let e: CalendarOutlineEvent = this;
        let days = 0;

        while (true) {
            if (processedIds.has(e.id)) {
                return { startRelativeTo: CalendarEventStartRelativeTo.startDate, days };
            }
            processedIds.add(e.id);

            let startRelativeTo = e.getStartRelativeTo();
            let startRelativeDays = e.getStartRelativeDays();
            days += startRelativeDays;
            if (startRelativeTo == CalendarEventStartRelativeTo.task) {
                let baseEvent = events.find(e2 => e2.id == e.startRelativeToEventId);
                if (baseEvent == null) {
                    return { startRelativeTo: CalendarEventStartRelativeTo.startDate, days };
                }
                e = baseEvent;        
            }
            else {
                return { startRelativeTo, days };    
            }
        }
    }

    getStartDateComparable(events: CalendarOutlineEvent[]): string {
        let startDate = this.getRelativeStartDate(events);
        return Objects.toString(startDate.startRelativeTo) + Objects.toString(startDate.days + 1_000_000).padStart(10, "0");    
    }

}
